import { ChevronLeftIcon } from "@heroicons/react/24/solid";
import { formatDate, WebhookEndpointId } from "@sp-crm/core";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ContentFull, ContentHeader } from "components/layout";
import { trpc } from "components/rpc/trpc";
import { InlineBanner } from "components/ui/inline-banner";
import { defaultLinkStyle } from "components/ui/link";
import { PrimaryButton } from "components/ui/primary-button";
import { Spinner } from "components/ui/spinner";
import { useMessage } from "hooks/use-message";
import React from "react";
import { Link, useRouteMatch } from "react-router-dom";
import { useFeature } from "store/selectors/hooks";
import { WebhookEventPayload } from "./webhook-event-payload";

export const ManageWebhookDetail: React.FC<unknown> = () => {
    const { params } = useRouteMatch<{ webhookId: WebhookEndpointId }>();
    const trpcClient = trpc.useTRPC();
    const isFeatureEnabled = useFeature("webhooks");
    const { message, setMessage } = useMessage();
    const [showSigningSecret, setShowSigningSecret] = React.useState(false);

    const webhookQuery = useQuery(
        trpcClient.webhooks.get.queryOptions({ id: params.webhookId }),
    );

    const deliveriesQuery = useQuery({
        ...trpcClient.webhooks.getDeliveries.queryOptions({
            id: params.webhookId,
            page: 1,
            pageSize: 25,
        }),
        enabled: !!webhookQuery.data,
    });

    const testWebhookMutation = useMutation({
        ...trpcClient.webhooks.test.mutationOptions(),
        onSuccess: () => {
            setMessage("success", "Test event sent successfully");
            deliveriesQuery.refetch();
        },
        onError: error => {
            setMessage("error", error.message || "Failed to send test event");
        },
    });

    if (!isFeatureEnabled) {
        return null;
    }

    return (
        <>
            <div className="mb-2">
                <Link to="/settings/integrations/webhooks" className={defaultLinkStyle}>
                    <div className="flex space-x-2 items-center">
                        <ChevronLeftIcon className="h-4 w-4" />
                        <span>Back to Webhooks</span>
                    </div>
                </Link>
            </div>
            <div className="space-y-4">
                <ContentFull>
                    <ContentHeader>Webhook Details</ContentHeader>
                    {webhookQuery.isLoading && <Spinner />}
                    {webhookQuery.isError && (
                        <InlineBanner type="error">
                            {webhookQuery.error.message || "Failed to load webhook"}
                        </InlineBanner>
                    )}
                    {webhookQuery.data ? (
                        <div className="space-y-6">
                            <div className="space-y-4">
                                <div>
                                    <div className="text-sm font-medium text-gray-700">
                                        Payload URL
                                    </div>
                                    <div className="mt-1 text-sm text-gray-900">
                                        {webhookQuery.data.url}
                                    </div>
                                </div>
                                <div>
                                    <div className="text-sm font-medium text-gray-700">
                                        Signing Secret
                                    </div>
                                    <div className="mt-1 text-sm text-gray-900 flex items-center gap-2">
                                        <span className="font-mono">
                                            {showSigningSecret
                                                ? webhookQuery.data.signingSecret
                                                : "••••••••••••••••••••"}
                                        </span>
                                        <button
                                            onClick={() =>
                                                setShowSigningSecret(!showSigningSecret)
                                            }
                                            className={defaultLinkStyle}>
                                            {showSigningSecret ? "Hide" : "Reveal"}
                                        </button>
                                    </div>
                                </div>
                                <div>
                                    <div className="text-sm font-medium text-gray-700">
                                        Created
                                    </div>
                                    <div className="mt-1 text-sm text-gray-900">
                                        {formatDate(
                                            new Date(webhookQuery.data.dateAdded),
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div className="flex items-center gap-3">
                                <PrimaryButton
                                    onClick={() =>
                                        testWebhookMutation.mutate({
                                            id: params.webhookId,
                                        })
                                    }
                                    disabled={testWebhookMutation.isPending}>
                                    {testWebhookMutation.isPending
                                        ? "Sending..."
                                        : "Send test event"}
                                </PrimaryButton>
                                {message && (
                                    <InlineBanner type={message.type}>
                                        {message.content}
                                    </InlineBanner>
                                )}
                            </div>
                        </div>
                    ) : null}
                </ContentFull>

                {webhookQuery.data && (
                    <ContentFull>
                        <ContentHeader>Recent Deliveries</ContentHeader>
                        {deliveriesQuery.isLoading ? (
                            <Spinner />
                        ) : !deliveriesQuery.data || deliveriesQuery.data.length === 0 ? (
                            <InlineBanner type="info">
                                No deliveries have been made to this webhook yet.
                            </InlineBanner>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Timestamp
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Status
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Payload
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Response Code
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Response Time
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {deliveriesQuery.data.map(delivery => (
                                            <tr key={delivery.id}>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                    {formatDate(
                                                        new Date(delivery.dateAdded),
                                                    )}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <span
                                                        className={`inline-flex px-2 text-xs font-semibold rounded-full ${
                                                            delivery.status === "success"
                                                                ? "bg-green-100 text-green-800"
                                                                : delivery.status ===
                                                                    "error"
                                                                  ? "bg-red-100 text-red-800"
                                                                  : "bg-yellow-100 text-yellow-800"
                                                        }`}>
                                                        {delivery.status}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 text-sm text-gray-900">
                                                    <WebhookEventPayload
                                                        payload={delivery.requestBody}
                                                    />
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                    {delivery.responseCode ?? "-"}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                    {delivery.responseTimeMs !== null
                                                        ? `${delivery.responseTimeMs}ms`
                                                        : "-"}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </ContentFull>
                )}
            </div>
        </>
    );
};
